import { Component, NgZone, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { TwoOptionModalComponent } from 'projects/common/src/app/components/two-option-modal/two-option-modal.component';
import { ITwoOptionModal } from 'projects/common/src/app/interfaces/components';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { excelExportDateFormat } from 'projects/common/src/app/services/utils/util.service';
import { IMachine } from 'src/app/modules/administration/interfaces/machine';
import { IFilterConfig } from 'src/app/modules/core/interfaces/filter-config';
import { IOutputFilter } from 'src/app/modules/core/interfaces/output-filter';
import { GoogleAnalyticsService } from 'src/app/modules/core/services/google/google-analytics.service';
import { ILayoutConfig } from 'src/app/modules/layout/interfaces/layout-config';
import { LayoutConfigService } from 'src/app/modules/layout/services/layout-config.service';
import { ExcelExportService } from '../../../../../../projects/common/src/app/services/utils/excel-export.service';
import { IVitalSignReportResponse } from '../../interfaces/vital-signs-report-response';
import { ReportVitalSignsService } from '../../services/report-vital-signs.service';

@Component({
  selector: 'app-report-vital-signs',
  templateUrl: './report-vital-signs.component.html',
  styleUrls: ['./report-vital-signs.component.scss'],
})
export class ReportVitalSignsComponent implements OnInit {
  public params: any;
  public selectedFilters: IOutputFilter;
  public numberOfReports: number;
  public fileNames: IVitalSignReportResponse[];
  public machines: IMachine[];
  public showAlert: boolean;
  public machineWithoutData: string;
  public filterConfig: IFilterConfig;

  constructor(
    private layoutConfigService: LayoutConfigService,
    private languageService: LanguageService,
    private modalService: NgbModal,
    private vitalSignsService: ReportVitalSignsService,
    private zone: NgZone,
    private googleAnalyticsService: GoogleAnalyticsService,
    private _excelExportService: ExcelExportService,
  ) {
    this.initDefaultFilterMachine();
    this.fileNames = [];
    this.machines = [];
    this.machineWithoutData = '';
  }

  /**
   * init default Filter machine
   */
  public initDefaultFilterMachine(): void {
    this.filterConfig = {
      dates: { required: true, options: { minMonth: 24 } },
      machineType: { required: true, multiselect: false, isForVitalSigns: true },
      machineList: { required: true, multiselect: true, isForVitalSigns: true },
      cleanButton: true,
      filterButton: {
        text: 'download-report',
        tooltip: 'Se descargará un archivo comprimido de reportes correspondiente a los datos ingresados en el filtro',
      },
    };
  }

  /**
   * On init component
   */
  public ngOnInit(): void {
    const config: ILayoutConfig = {
      title: 'reports',
      subtitle: 'report-vital-signs',
      imageKey: 'report',
      imageType: 'section',
    };

    this.layoutConfigService.setConfig(config);
  }

  /**
   * Function to get report data from received filters
   *
   * @param filters Filters selected to get data
   */
  public async getReportByFilters(selectedFilters: IOutputFilter): Promise<void> {
    this.machineWithoutData = '';
    this.filterConfig.isLoading = true;
    this.selectedFilters = selectedFilters;
    this.vitalSignsService.getMachinesForVitalSigns(+selectedFilters.machineTypeId).then((res) => {
      this.machines = res;
    });

    this.params = this.vitalSignsService.setParams(selectedFilters);
    await this.downloadReport()
      .then(
        () => {
          this.vitalSignsService.getVitalSignsReportInfo(selectedFilters).then((res) => {
            this.zone.run(() => {
              this.fileNames = res.data;
              this.showAlert = true;
              this.numberOfReports = this.fileNames.length;
              this.machines.forEach((element) => {
                const noDataMachine = this.fileNames.filter((v) => v.machineId === element.machineId);
                if (noDataMachine.length === 0) {
                  this.machineWithoutData += `${element.name}, `;
                }
              });
              this.machineWithoutData = `${this.machineWithoutData.substring(0, this.machineWithoutData.length - 2)}.`;
            });
          });
        },
        () => {},
      )
      // eslint-disable-next-line no-return-assign
      .finally(() => (this.filterConfig.isLoading = false));
    const filters = {
      reports: 'report-vital-signs',
    };
    this.googleAnalyticsService.sendCustomEvent('filter', filters);
  }

  /**
   * Function to download report
   */
  public async downloadReport(): Promise<void> {
    this.vitalSignsService.getVitalSignsReport(this.selectedFilters).then(
      (resp) => {
        if (resp.type === 'application/zip') {
          const startDate = excelExportDateFormat(this.selectedFilters.startDate);
          const endDate = excelExportDateFormat(this.selectedFilters.endDate);
          const blobResponse = resp;
          const blobFile = new Blob([blobResponse], {
            type: 'application/zip, application/octet-stream, application/x-zip-compressed, multipart/x-zip',
          });
          const dateFileName = `${startDate} - ${endDate}`;
          const filename = `${this.languageService.message('vital-signs-report-filename')} - ${dateFileName}`;
          const file = new File([blobFile], `${filename}.zip`, {
            type: 'application/zip, application/octet-stream, application/x-zip-compressed, multipart/x-zip',
          });
          saveAs(file);
          const excelDownload = {
            report: `${this.languageService.message('vital-signs-report-filename')}`,
          };
          this.googleAnalyticsService.sendExcelDownloadEvent(excelDownload);
        } else {
          const componentRef = this.modalService.open(TwoOptionModalComponent, {
            centered: true,
          });

          const twoOption: ITwoOptionModal = {
            title: 'error-downloading-report',
            message: 'no data for the specified filter',
            imageKey: 'error-red',
            imageType: 'icon',
            darkTheme: true,
          };

          componentRef.componentInstance.option = twoOption;
        }
        this._excelExportService.messageToastSuccess();
      },
      (error) => {
        console.log(error);
        this._excelExportService.messageToastError();
      },
    );
  }
}
